// src/pages/Home/TensorPilotHomeSection/InteractiveAppTour.js
// Guided, auto-advancing interactive product tour rendered inside a realistic
// desktop-window frame. Plays like a short movie the moment it scrolls into
// view, but any click/keypress hands control to the visitor.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock, faShieldAlt, faDownload, faArrowRight, faCheck, faBolt, faSync,
  faLayerGroup, faBrain, faTrophy, faThumbsUp, faMicrophone, faImage, faVideo,
  faPlay, faPause, faChevronLeft, faChevronRight, faKey, faChartBar,
  faCircleNotch, faRoute, faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { faApple, faWindows, faLinux } from '@fortawesome/free-brands-svg-icons';

import './css/InteractiveAppTour.css';

const STEP_DURATION = 7600; // ms per auto-advanced step
const TICK = 50;

// ─────────────────────────────────────────────────────────────────────────
// Step 1 — BYOK + OS Keychain
// ─────────────────────────────────────────────────────────────────────────
const BYOK_ROWS = [
  { idx: 1, key: 'sk-••••••••••••••••aBk3' },
  { idx: 0, key: 'AIza••••••••••••••X9j2' },
  { idx: 2, key: 'sk-ant-••••••••••••G7mQ' },
  { idx: 5, key: 'co-••••••••••••••••P4vT' },
  { idx: 7, key: 'sk-or-v1-••••••••••K9n2' },
];

const ByokPanel = ({ providers, connected, onConnect, connecting }) => (
  <div className="tpt-panel">
    <div className="tpt-panel-head">
      <FontAwesomeIcon icon={faKey} className="tpt-head-icon tpt-i-green" />
      <span>API Keys Vault</span>
      <span className="tpt-badge tpt-badge-green">OS Keychain</span>
      <span className="tpt-badge">Never transmitted</span>
    </div>
    <div className="tpt-keys">
      {BYOK_ROWS.map((row) => {
        const p = providers[row.idx];
        if (!p) return null;
        const isConnected = connected.includes(p.key);
        const isConnecting = connecting === p.key;
        return (
          <div key={p.key} className={`tpt-key-row ${isConnected ? 'is-connected' : ''}`}>
            <img src={p.logo} alt={p.name} className="tpt-key-logo" />
            <div className="tpt-key-info">
              <strong>{p.name}</strong>
              <code>{isConnected ? row.key : 'Not connected'}</code>
            </div>
            {isConnected ? (
              <span className="tpt-key-status"><FontAwesomeIcon icon={faCheck} /> Active</span>
            ) : (
              <button
                type="button"
                className="tpt-connect-btn"
                onClick={() => onConnect(p.key)}
                disabled={isConnecting}
              >
                {isConnecting
                  ? <><FontAwesomeIcon icon={faCircleNotch} spin /> Validating</>
                  : <>Connect <span className="tpt-try-ring" aria-hidden /></>}
              </button>
            )}
          </div>
        );
      })}
    </div>
    <div className="tpt-keys-foot">
      <FontAwesomeIcon icon={faLock} className="me-2" />
      macOS Keychain · Windows Credential Manager · libsecret (Linux)
      <span className="tpt-keys-note">Keys stay on your device. Calls go straight to the provider — never through our servers.</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Step 2 — Model Arena (editable prompt + word-by-word streaming)
// ─────────────────────────────────────────────────────────────────────────
const DEFAULT_ARENA_PROMPT = 'Summarize the data-sovereignty risks in our vendor contracts and suggest mitigations.';
const ARENA_RESULTS = [
  {
    idx: 2, model: 'Claude Sonnet 4.5',
    text: 'Three data-residency risks found: a US-East processing clause in Vendor_Agreement_v2, no deletion timeline in the SaaS MSA, and a missing BAA with the telemetry sub-processor. Add a residency addendum plus an annual audit clause.',
    cost: '$0.003', latency: '1.4s', speed: 1.0,
  },
  {
    idx: 0, model: 'Gemini 2.5 Flash',
    text: 'Main risks: cross-border transfer without explicit consent, sub-processor exposure, and no breach-notification SLA. Attach a PIPEDA data-processing addendum to every active vendor contract.',
    cost: '$0.0004', latency: '0.6s', speed: 1.7,
  },
  {
    idx: 1, model: 'GPT-5',
    text: 'Prioritized: telemetry clause (high), deletion ambiguity (medium), sub-processor BAA gap (medium). Redline language for clauses 4.2 and 7 is drafted and ready to export to your contract workspace.',
    cost: '$0.011', latency: '2.1s', speed: 0.8,
  },
];

const ArenaPanel = ({ providers, prompt, phase, stream, winner, onPromptChange, onFocus, onRun, onVote }) => {
  const busy = phase === 'typing' || phase === 'running';
  return (
    <div className="tpt-panel">
      <div className="tpt-panel-head">
        <FontAwesomeIcon icon={faTrophy} className="tpt-head-icon tpt-i-amber" />
        <span>Model Arena</span>
        <span className="tpt-badge">3 models competing</span>
        {phase !== 'idle' && <span className="tpt-badge tpt-badge-green">Live</span>}
      </div>
      <div className="tpt-arena-prompt">
        <span className="tpt-arena-plabel">Prompt</span>
        <input
          className="tpt-arena-input"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={(e) => { if (e.key === 'Enter') onRun(); }}
          placeholder="Ask anything…"
          aria-label="Arena prompt"
        />
        <button type="button" className="tpt-run-btn" onClick={onRun} disabled={busy}>
          {phase === 'running'
            ? <><FontAwesomeIcon icon={faCircleNotch} spin /> Streaming</>
            : phase === 'typing'
              ? <><FontAwesomeIcon icon={faCircleNotch} spin /> …</>
              : <><FontAwesomeIcon icon={faPlay} /> Run <span className="tpt-try-ring" aria-hidden /></>}
        </button>
      </div>
      <div className="tpt-arena-grid">
        {ARENA_RESULTS.map((r, i) => {
          const p = providers[r.idx];
          const isWinner = winner === r.idx;
          const words = r.text.split(' ');
          const shown = phase === 'done'
            ? words.length
            : phase === 'running'
              ? Math.min(words.length, Math.floor(stream * r.speed))
              : 0;
          const partial = words.slice(0, shown).join(' ');
          const streaming = phase === 'running' && shown < words.length;
          return (
            <div key={r.model} className={`tpt-arena-col ${isWinner ? 'is-winner' : ''}`}>
              <div className="tpt-arena-colhead">
                <img src={p ? p.logo : ''} alt={r.model} className="tpt-arena-logo" />
                <span>{r.model}</span>
                {isWinner && <FontAwesomeIcon icon={faTrophy} className="tpt-winner-ico" />}
              </div>
              <div className="tpt-arena-resp">
                {phase === 'idle' || phase === 'typing' ? (
                  <span className="tpt-arena-await">Waiting for prompt…</span>
                ) : (
                  <>{partial}{streaming && <span className="tpt-caret" />}</>
                )}
              </div>
              <div className="tpt-arena-meta">
                <span>{r.cost}/query</span>
                <span>{r.latency}</span>
                {phase === 'done' && (
                  <button
                    type="button"
                    className={`tpt-vote ${isWinner ? 'is-picked' : ''}`}
                    onClick={() => onVote(r.idx)}
                  >
                    <FontAwesomeIcon icon={isWinner ? faTrophy : faThumbsUp} /> {isWinner ? 'Winner' : 'Pick'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Step 3 — Working Set
// ─────────────────────────────────────────────────────────────────────────
const WS_FILES = [
  { name: 'auth_service.py', status: 'CACHED', tokens: 1120, size: '4.2 KB' },
  { name: 'billing_routes.py', status: 'CACHED', tokens: 1640, size: '6.1 KB' },
  { name: 'user_model.py', status: 'CACHED', tokens: 760, size: '2.8 KB' },
  { name: 'config.yaml', status: 'CACHED', tokens: 300, size: '1.1 KB' },
  { name: 'api_gateway.py', status: 'UPDATED', tokens: 1520, size: '5.7 KB' },
  { name: 'test_auth.py', status: 'NEW', tokens: 1320, size: '4.9 KB' },
];
const WS_RATE = 0.000003; // $/token, illustrative blended rate
const WS_MSGS = 1200; // messages / month

const WorkingSetPanel = ({ excluded, onToggle }) => {
  const included = WS_FILES.filter((f) => !excluded.includes(f.name));
  const baselineTokens = included.reduce((s, f) => s + f.tokens, 0);
  const sentTokens = included.reduce((s, f) => s + (f.status === 'CACHED' ? 0 : f.tokens), 0);
  const savings = baselineTokens ? Math.round((1 - sentTokens / baselineTokens) * 100) : 0;
  const monthly = (sentTokens * WS_RATE * WS_MSGS).toFixed(2);
  const baselineMonthly = (baselineTokens * WS_RATE * WS_MSGS).toFixed(2);
  const ring = `conic-gradient(#34d399 ${savings * 3.6}deg, rgba(255,255,255,0.08) 0deg)`;

  return (
    <div className="tpt-panel">
      <div className="tpt-panel-head">
        <FontAwesomeIcon icon={faLayerGroup} className="tpt-head-icon tpt-i-blue" />
        <span>Working Set</span>
        <span className="tpt-badge">{included.length} files pinned</span>
        <span className="tpt-badge tpt-badge-green">Cost optimized</span>
      </div>
      <div className="tpt-ws-body">
        <div className="tpt-ws-files">
          {WS_FILES.map((f) => {
            const off = excluded.includes(f.name);
            return (
              <button
                type="button"
                key={f.name}
                className={`tpt-ws-file ${off ? 'is-off' : ''} tpt-ws-${f.status.toLowerCase()}`}
                onClick={() => onToggle(f.name)}
              >
                <span className="tpt-ws-eye">
                  <FontAwesomeIcon icon={f.status === 'CACHED' ? faBolt : faSync} />
                </span>
                <span className="tpt-ws-name">{f.name}<small>{f.size}</small></span>
                <span className={`tpt-ws-tag tpt-tag-${f.status.toLowerCase()}`}>{f.status}</span>
                <span className="tpt-ws-tok">{f.status === 'CACHED' ? '0' : `~${f.tokens}`} tok</span>
              </button>
            );
          })}
        </div>
        <div className="tpt-ws-meter">
          <div className="tpt-ring" style={{ background: ring }}>
            <div className="tpt-ring-hole">
              <strong>{savings}%</strong>
              <span>tokens<br />saved</span>
            </div>
          </div>
          <div className="tpt-ws-cost">
            <div className="tpt-ws-cost-row">
              <span>Without caching</span>
              <s>${baselineMonthly}/mo</s>
            </div>
            <div className="tpt-ws-cost-row is-live">
              <span>With Working Set</span>
              <strong>${monthly}/mo</strong>
            </div>
            <p className="tpt-ws-hint">Toggle a file to see cost update. Unchanged files are cached — you pay only for what changed.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Step 4 — AI Memory
// ─────────────────────────────────────────────────────────────────────────
const MEMORIES = [
  { id: 0, type: 'preference', tier: 1, text: 'Prefers Canadian data-residency for all client work' },
  { id: 1, type: 'decision', tier: 2, text: 'Chose Gemini Flash as the default drafting model' },
  { id: 2, type: 'fact', tier: 2, text: 'Team of 50; bills AI cost back per matter code' },
  { id: 3, type: 'instruction', tier: 3, text: 'Always cite the source file when answering from the KB' },
];
const MEM_TIERS = [
  { n: 1, name: 'User Profile', sub: 'Always active · injected every prompt', speed: '<1ms' },
  { n: 2, name: 'Local Vector Memory', sub: '10–20 memories · on-device search', speed: '<10ms' },
  { n: 3, name: 'Deep Retrieval', sub: 'Provider-hosted RAG when needed', speed: '2–5s' },
];

const MemoryPanel = ({ active, onPick }) => {
  const activeTier = active == null ? null : MEMORIES[active].tier;
  return (
    <div className="tpt-panel">
      <div className="tpt-panel-head">
        <FontAwesomeIcon icon={faBrain} className="tpt-head-icon tpt-i-purple" />
        <span>AI Memory</span>
        <span className="tpt-badge">Works with any provider</span>
        <span className="tpt-badge tpt-badge-green">Local-first</span>
      </div>
      <div className="tpt-mem-body">
        <div className="tpt-mem-list">
          <div className="tpt-mem-list-label">Click a memory to inject it</div>
          {MEMORIES.map((m) => (
            <button
              type="button"
              key={m.id}
              className={`tpt-mem-item ${active === m.id ? 'is-active' : ''}`}
              onClick={() => onPick(m.id)}
            >
              <span className={`tpt-mem-type tpt-mt-${m.type}`}>{m.type}</span>
              <span className="tpt-mem-text">{m.text}</span>
              {active === m.id && <FontAwesomeIcon icon={faArrowRight} className="tpt-mem-arrow" />}
            </button>
          ))}
        </div>
        <div className="tpt-mem-tiers">
          {MEM_TIERS.map((t) => (
            <div key={t.n} className={`tpt-mem-tier ${activeTier === t.n ? 'is-lit' : ''}`}>
              <div className="tpt-mem-tier-num">{t.n}</div>
              <div className="tpt-mem-tier-info">
                <strong>{t.name}</strong>
                <span>{t.sub}</span>
              </div>
              <div className="tpt-mem-tier-speed">{t.speed}</div>
            </div>
          ))}
          <div className="tpt-mem-context">
            {active == null
              ? <span className="tpt-mem-context-empty">Context bar builds here as memories are recalled…</span>
              : <><FontAwesomeIcon icon={faCheck} className="tpt-i-green me-2" />Injected into the prompt under your API key — no data sold, no training.</>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Step 5 — Workflows (saved instructions + quick actions)
// Names/actions mirror gcf/functions/Data/builtin_workflows.json
// ─────────────────────────────────────────────────────────────────────────
const TOUR_WORKFLOWS = [
  {
    id: 0,
    name: 'Reality Check',
    emoji: '🧐',
    category: 'Operations',
    desc: 'Verify real-world claims with search before calling anything fake or fictional.',
    systemHint: 'Search before asserting anything is fictional, fake, or non-existent…',
    actions: [
      { label: 'Fact-check this content', icon: '🔎', promptPreview: 'Review the attached content — verify each real-world claim with Google Search before commenting…' },
      { label: 'Legal/safety review (verified)', icon: '⚖️', promptPreview: 'Legal review — verify references first with search, then analyze. Never assume something is a mock-up…' },
      { label: 'Is this document real?', icon: '📄', promptPreview: 'Search for this document or filing number before summarizing. Separate what it says from what you verified…' },
    ],
  },
  {
    id: 1,
    name: 'TensorPilot Guide',
    emoji: '📘',
    category: 'Product Help',
    desc: 'In-app help — turn it on, ask anything, get plain-English answers.',
    systemHint: 'You are TensorPilot\'s friendly in-app guide — plain English, no jargon…',
    actions: [
      { label: 'My first 10 minutes', icon: '🚀', promptPreview: 'Walk me through my first ten minutes, ending with a message actually sent and answered…' },
      { label: 'Ask about my own file', icon: '📎', promptPreview: 'Walk me through adding one of my files and what to type, and which file types work…' },
      { label: 'Can TensorPilot do this?', icon: '❓', promptPreview: 'Tell me honestly whether TensorPilot can do this today, and if not, the closest thing that works…' },
    ],
  },
  {
    id: 2,
    name: 'Litigation: Factum Citation Checker',
    emoji: '⚖️',
    category: 'Legal',
    desc: 'Cross-reference Factum claims against Affidavit paragraphs to catch citation errors.',
    systemHint: 'Strictly verify legal citations — Factum claim vs cited Affidavit paragraph…',
    actions: [
      { label: 'Verify Citations', icon: '🔍', promptPreview: 'Cross-reference all paragraph citations in the Factum against the Affidavit. Flag factual discrepancies…' },
      { label: 'Opposing issues', icon: '🎯', promptPreview: 'Read the Factum as opposing counsel would. List the top vulnerabilities in a table…' },
      { label: 'Client update bullets', icon: '✉️', promptPreview: 'Draft 6–8 neutral client email bullets on how the Factum aligns with the Affidavit…' },
    ],
  },
  {
    id: 3,
    name: 'Enterprise Legal',
    emoji: '📑',
    category: 'Legal',
    desc: 'DPA, M&A term sheets, and trust memos — add documents to your Working Set first.',
    systemHint: 'Senior legal-analyst assistant — precise, structured, not legal advice…',
    actions: [
      { label: 'DPA: indemnity & model training', icon: '📋', promptPreview: 'Extract indemnification clauses and flag any sections allowing sub-processors to train on our data…' },
      { label: 'M&A: caps & buyer risks', icon: '🤝', promptPreview: 'Compare liability caps and survival periods. Summarize the risks for the buyer…' },
      { label: 'HNWI: trust transfer (p. 12)', icon: '🏛️', promptPreview: 'Identify tax liabilities triggered by moving real estate into the irrevocable trust on page 12…' },
    ],
  },
];

const WorkflowsPanel = ({ active, action, onPick, onActionPick }) => {
  const wf = TOUR_WORKFLOWS[active] || TOUR_WORKFLOWS[0];
  const staged = action != null ? wf.actions[action] : null;
  return (
    <div className="tpt-panel">
      <div className="tpt-panel-head">
        <FontAwesomeIcon icon={faRoute} className="tpt-head-icon tpt-i-blue" />
        <span>Workflows</span>
        <span className="tpt-badge">Saved instructions + quick actions</span>
        <span className="tpt-badge tpt-badge-green">Cloud-synced</span>
      </div>
      <div className="tpt-wf-body">
        <div className="tpt-wf-left">
          <div className="tpt-wf-list-label">Built-in presets</div>
          <div className="tpt-wf-list">
            {TOUR_WORKFLOWS.map((w) => (
              <button
                type="button"
                key={w.id}
                className={`tpt-wf-item ${active === w.id ? 'is-active' : ''}`}
                onClick={() => onPick(w.id)}
              >
                <span className="tpt-wf-item-emoji" aria-hidden>{w.emoji}</span>
                <span>{w.name}</span>
                {active === w.id && <span className="tpt-try-ring" aria-hidden />}
              </button>
            ))}
          </div>
          <div className="tpt-wf-detail">
            <span className="tpt-wf-cat">{wf.category}</span>
            <p className="tpt-wf-desc">{wf.desc}</p>
            <div className="tpt-wf-actions">
              {wf.actions.map((a) => (
                <span key={a.label} className="tpt-wf-chip">{a.icon} {a.label}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="tpt-wf-right">
          <div className="tpt-wf-preview">
            <div className="tpt-wf-preview-label">In chat</div>
            <div className="tpt-wf-active-bar">
              <span className="tpt-wf-active-emoji" aria-hidden>{wf.emoji}</span>
              <div className="tpt-wf-active-info">
                <strong>{wf.name}</strong>
                <span>Workflow active</span>
              </div>
              <span className="tpt-wf-active-badge">Active</span>
            </div>
            <div className="tpt-wf-inject">
              <FontAwesomeIcon icon={faSync} className="tpt-wf-inject-ico" />
              <span>{wf.systemHint}</span>
            </div>
            <div className="tpt-wf-qa-preview" role="group" aria-label={`${wf.name} quick actions`}>
              {wf.actions.map((a, i) => (
                <button
                  type="button"
                  key={a.label}
                  className={`tpt-wf-qa-btn ${action === i ? 'is-active' : ''}`}
                  onClick={() => onActionPick(i)}
                >
                  {a.icon} {a.label}
                </button>
              ))}
            </div>
            <div className="tpt-wf-input-preview">
              <span className="tpt-wf-input-label">Your message</span>
              <div className={`tpt-wf-input-box ${staged ? 'has-text' : ''}`}>
                {staged ? (
                  <>
                    <span className="tpt-wf-prompt-text">{staged.promptPreview}</span>
                    <span className="tpt-wf-cursor" aria-hidden />
                  </>
                ) : (
                  <span className="tpt-wf-input-hint">Click a quick action to stage a prompt…</span>
                )}
              </div>
              <button type="button" className="tpt-wf-send" disabled={!staged} aria-hidden={!staged}>
                <FontAwesomeIcon icon={faPaperPlane} /> Send
              </button>
            </div>
            <p className="tpt-wf-preview-foot">
              Not automation — activate a preset, tap a button, then send like any chat.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Step 6 — Media Studio
// ─────────────────────────────────────────────────────────────────────────
const MEDIA_TABS = [
  { id: 'image', label: 'Image', icon: faImage },
  { id: 'video', label: 'Video', icon: faVideo },
  { id: 'voice', label: 'Voice', icon: faMicrophone },
];
const IMG_TILES = [
  { g: 'linear-gradient(135deg,#6366f1,#8b5cf6,#ec4899)', model: 'Imagen 4' },
  { g: 'linear-gradient(135deg,#0ea5e9,#22d3ee,#34d399)', model: 'DALL·E 3' },
  { g: 'linear-gradient(135deg,#f59e0b,#ef4444,#8b5cf6)', model: 'Grok Imagine' },
  { g: 'linear-gradient(135deg,#10b981,#3b82f6,#6366f1)', model: 'Seedream' },
];

const MediaPanel = ({ tab, phase, onTab, onGenerate }) => (
  <div className="tpt-panel">
    <div className="tpt-panel-head">
      <FontAwesomeIcon icon={faImage} className="tpt-head-icon tpt-i-pink" />
      <span>Media Studio</span>
      <span className="tpt-badge">Image · Video · Voice</span>
      {phase === 'done' && <span className="tpt-badge tpt-badge-green">Rendered</span>}
    </div>
    <div className="tpt-media-tabs">
      {MEDIA_TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`tpt-media-tab ${tab === t.id ? 'is-active' : ''}`}
          onClick={() => onTab(t.id)}
        >
          <FontAwesomeIcon icon={t.icon} /> {t.label}
        </button>
      ))}
      <button type="button" className="tpt-run-btn tpt-media-run" onClick={onGenerate}>
        {phase === 'gen'
          ? <><FontAwesomeIcon icon={faCircleNotch} spin /> Generating</>
          : <><FontAwesomeIcon icon={faBolt} /> Generate <span className="tpt-try-ring" aria-hidden /></>}
      </button>
    </div>

    {tab === 'image' && (
      <div className="tpt-img-grid">
        {IMG_TILES.map((tile, i) => (
          <div key={tile.model} className={`tpt-img-tile ${phase === 'done' ? 'is-done' : ''} ${phase === 'gen' ? 'is-gen' : ''}`}
               style={{ '--tile-g': tile.g, animationDelay: `${i * 120}ms` }}>
            {phase === 'done'
              ? <span className="tpt-img-model">{tile.model}</span>
              : <span className="tpt-img-shimmer" />}
          </div>
        ))}
        <span className="tpt-media-caption">Same prompt, four image models — pick your favourite (Image Arena).</span>
      </div>
    )}

    {tab === 'video' && (
      <div className="tpt-video-stage">
        <div className={`tpt-video-frame ${phase === 'done' ? 'is-done' : ''} ${phase === 'gen' ? 'is-gen' : ''}`}>
          {phase === 'done'
            ? <div className="tpt-video-play"><FontAwesomeIcon icon={faPlay} /></div>
            : <span className="tpt-img-shimmer" />}
          <span className="tpt-video-tag">Veo 3.1 · 1080p · 5s</span>
        </div>
        <span className="tpt-media-caption">Text-to-video and image-to-video with audio, from Veo, Sora & more.</span>
      </div>
    )}

    {tab === 'voice' && (
      <div className="tpt-voice-stage">
        <div className={`tpt-voice-wave ${phase !== 'idle' ? 'is-live' : ''}`}>
          {Array.from({ length: 28 }).map((_, i) => (
            <i key={i} style={{ animationDelay: `${(i % 7) * 90}ms` }} />
          ))}
        </div>
        <div className="tpt-voice-meta">
          <span className="tpt-voice-name">Gemini TTS · voice "Kore"</span>
          <button type="button" className="tpt-voice-play"><FontAwesomeIcon icon={faPlay} /></button>
        </div>
        <span className="tpt-media-caption">Natural text-to-speech in dozens of voices — Expressive to Professional.</span>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Step 7 — Usage / Cost
// ─────────────────────────────────────────────────────────────────────────
const COST_DATA = {
  '1D': { user: 3.1, system: 0.7, media: 1.4, total: '5.20' },
  '7D': { user: 21.4, system: 4.8, media: 9.6, total: '35.80' },
  '1M': { user: 88.0, system: 19.5, media: 41.2, total: '148.70' },
};
const COST_MAX = 90;
const COST_SERIES = [
  { key: 'user', label: 'Chat', cls: 'tpt-bar-user' },
  { key: 'system', label: 'Memory / system', cls: 'tpt-bar-system' },
  { key: 'media', label: 'Media', cls: 'tpt-bar-media' },
];

const CostPanel = ({ period, onPeriod }) => {
  const d = COST_DATA[period];
  return (
    <div className="tpt-panel">
      <div className="tpt-panel-head">
        <FontAwesomeIcon icon={faChartBar} className="tpt-head-icon tpt-i-green" />
        <span>Usage &amp; Cost</span>
        <span className="tpt-badge">Per-model transparency</span>
        <span className="tpt-badge tpt-badge-green">You control spend</span>
      </div>
      <div className="tpt-cost-controls">
        {Object.keys(COST_DATA).map((p) => (
          <button
            key={p}
            type="button"
            className={`tpt-period ${period === p ? 'is-active' : ''}`}
            onClick={() => onPeriod(p)}
          >
            {p}
          </button>
        ))}
        <span className="tpt-cost-total">${d.total}<small>total spend</small></span>
      </div>
      <div className="tpt-cost-chart">
        {COST_SERIES.map((s) => (
          <div key={s.key} className="tpt-cost-group">
            <div className="tpt-cost-track">
              <div
                className={`tpt-cost-bar ${s.cls}`}
                style={{ height: `${Math.min(100, (d[s.key] / COST_MAX) * 100)}%` }}
              >
                <span className="tpt-cost-val">${d[s.key].toFixed(1)}</span>
              </div>
            </div>
            <span className="tpt-cost-label">{s.label}</span>
          </div>
        ))}
      </div>
      <p className="tpt-cost-foot">Chat vs. background memory vs. media — every cent, itemized by model. No seat fees.</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Outro — Download CTA
// ─────────────────────────────────────────────────────────────────────────
const OutroPanel = () => (
  <div className="tpt-panel tpt-outro">
    <div className="tpt-outro-inner">
      <div className="tpt-outro-badge"><FontAwesomeIcon icon={faShieldAlt} /> Privacy-first · BYOK</div>
      <h3>Your keys. Your files. Your AI.</h3>
      <p>Everything you just tried runs locally, direct to the providers you choose. Free to download.</p>
      <div className="tpt-outro-chips">
        <span><FontAwesomeIcon icon={faTrophy} /> Compare any model</span>
        <span><FontAwesomeIcon icon={faBolt} /> 84% fewer tokens</span>
        <span><FontAwesomeIcon icon={faLock} /> Keys never leave device</span>
      </div>
      <div className="tpt-outro-ctas">
        <Link to="/download" className="tpt-dl-btn tpt-dl-primary">
          <FontAwesomeIcon icon={faApple} /><FontAwesomeIcon icon={faWindows} /><FontAwesomeIcon icon={faLinux} />
          Download free
        </Link>
        <Link to="/download" className="tpt-dl-btn tpt-dl-ghost">
          See all platforms <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Main tour component
// ─────────────────────────────────────────────────────────────────────────
const InteractiveAppTour = ({ providers = [] }) => {
  const initialByok = useMemo(
    () => [providers[1] && providers[1].key, providers[0] && providers[0].key].filter(Boolean),
    [providers]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [inView, setInView] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [progress, setProgress] = useState(0);

  // Per-step interaction state
  const [byokConnected, setByokConnected] = useState(initialByok);
  const [byokConnecting, setByokConnecting] = useState(null);
  const [arenaPrompt, setArenaPrompt] = useState(DEFAULT_ARENA_PROMPT);
  const [arenaPhase, setArenaPhase] = useState('idle'); // idle | typing | running | done
  const [arenaStream, setArenaStream] = useState(0);
  const [arenaWinner, setArenaWinner] = useState(null);
  const [wsExcluded, setWsExcluded] = useState([]);
  const [memActive, setMemActive] = useState(null);
  const [wfActive, setWfActive] = useState(0);
  const [wfAction, setWfAction] = useState(null);
  const [mediaTab, setMediaTab] = useState('image');
  const [mediaPhase, setMediaPhase] = useState('idle'); // idle | gen | done
  const [costPeriod, setCostPeriod] = useState('7D');

  const rootRef = useRef(null);
  const timersRef = useRef([]);
  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);
  const later = useCallback((fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);

  const steps = useMemo(() => ([
    {
      id: 'byok', tab: 'Keys', icon: faKey,
      headline: 'Bring your own keys',
      sub: 'Stored in your OS keychain. Calls go straight to the provider — never through our servers.',
      chip: '0 data through our servers',
    },
    {
      id: 'arena', tab: 'Arena', icon: faTrophy,
      headline: 'Ask once. Compare every model.',
      sub: 'Type a prompt, run it across Claude, GPT and Gemini side by side, then pick the winner.',
      chip: '9 providers, 1 window',
    },
    {
      id: 'workingset', tab: 'Files', icon: faLayerGroup,
      headline: 'Pin files once. Pay for what changed.',
      sub: 'Unchanged files are cached locally, so repeat context stops costing you tokens.',
      chip: 'Up to 84% fewer tokens',
    },
    {
      id: 'memory', tab: 'Memory', icon: faBrain,
      headline: 'It remembers you — on your terms',
      sub: 'A profile, on-device vector memory, and deep retrieval when it helps. All local-first.',
      chip: '<10ms local recall',
    },
    {
      id: 'workflows', tab: 'Workflows', icon: faRoute,
      headline: 'Saved instructions + one-click actions',
      sub: 'Pick a built-in preset or build your own — a system prompt plus shortcut buttons, synced across devices.',
      chip: 'Activate once, reuse daily',
    },
    {
      id: 'media', tab: 'Media', icon: faImage,
      headline: 'Create images, video and voice',
      sub: 'Generate with Imagen, DALL·E, Veo, Sora and more — and compare image models head-to-head.',
      chip: 'Image · Video · Voice',
    },
    {
      id: 'cost', tab: 'Cost', icon: faChartBar,
      headline: 'See every cent you spend',
      sub: 'Chat vs. background memory vs. media, itemized by model. No per-seat fees, ever.',
      chip: 'Full cost transparency',
    },
    {
      id: 'outro', tab: 'Get it', icon: faDownload,
      headline: 'Ready when you are',
      sub: 'Free for Windows, macOS and Linux. Bring your own API keys and go.',
      chip: 'Free download',
    },
  ]), []);

  const stepCount = steps.length;
  const step = steps[activeStep];
  const effectivePlaying = isPlaying && inView && !reduceMotion;

  // prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  // Autostart the "movie" once (most of) the window is on screen
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting)),
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Reset interaction state whenever a step becomes active
  const resetStepState = useCallback((id) => {
    if (id === 'byok') { setByokConnected(initialByok); setByokConnecting(null); }
    if (id === 'arena') { setArenaPrompt(DEFAULT_ARENA_PROMPT); setArenaPhase('idle'); setArenaStream(0); setArenaWinner(null); }
    if (id === 'workingset') setWsExcluded([]);
    if (id === 'memory') setMemActive(null);
    if (id === 'workflows') { setWfActive(0); setWfAction(null); }
    if (id === 'media') { setMediaTab('image'); setMediaPhase('idle'); }
    if (id === 'cost') setCostPeriod('7D');
  }, [initialByok]);

  const goTo = useCallback((idx) => {
    clearTimers();
    const nextIdx = (idx + stepCount) % stepCount;
    setProgress(0);
    resetStepState(steps[nextIdx].id);
    setActiveStep(nextIdx);
  }, [clearTimers, resetStepState, stepCount, steps]);

  const next = useCallback(() => goTo(activeStep + 1), [goTo, activeStep]);
  const prev = useCallback(() => goTo(activeStep - 1), [goTo, activeStep]);
  const takeControl = useCallback(() => setIsPlaying(false), []);

  // Auto-advance progress bar
  useEffect(() => {
    if (!effectivePlaying) return undefined;
    setProgress(0);
    const started = Date.now();
    const iv = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - started) / STEP_DURATION) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(iv);
        goTo(activeStep + 1);
      }
    }, TICK);
    return () => clearInterval(iv);
  }, [effectivePlaying, activeStep, goTo]);

  // Arena: typewriter the prompt, then hand off to streaming
  useEffect(() => {
    if (arenaPhase !== 'typing') return undefined;
    setArenaPrompt('');
    let i = 0;
    const iv = setInterval(() => {
      i += 1;
      setArenaPrompt(DEFAULT_ARENA_PROMPT.slice(0, i));
      if (i >= DEFAULT_ARENA_PROMPT.length) {
        clearInterval(iv);
        setArenaPhase('running');
      }
    }, 26);
    return () => clearInterval(iv);
  }, [arenaPhase]);

  // Arena: word-by-word streaming across the three columns
  useEffect(() => {
    if (arenaPhase !== 'running') return undefined;
    setArenaStream(0);
    const maxTicks = Math.max(
      ...ARENA_RESULTS.map((r) => Math.ceil(r.text.split(' ').length / r.speed))
    );
    let t = 0;
    const iv = setInterval(() => {
      t += 1;
      setArenaStream(t);
      if (t >= maxTicks + 1) {
        clearInterval(iv);
        setArenaPhase('done');
      }
    }, 60);
    return () => clearInterval(iv);
  }, [arenaPhase]);

  // Arena: auto-pick a winner after streaming completes (only while playing)
  useEffect(() => {
    if (arenaPhase === 'done' && effectivePlaying && arenaWinner == null) {
      const id = setTimeout(() => setArenaWinner(0), 1000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [arenaPhase, effectivePlaying, arenaWinner]);

  // Auto-demo the current step's interaction while playing
  useEffect(() => {
    if (!effectivePlaying) return undefined;
    clearTimers();
    const id = step.id;
    if (id === 'byok') {
      const order = [2, 5, 7];
      order.forEach((pIdx, i) => {
        const key = providers[pIdx] && providers[pIdx].key;
        if (!key) return;
        later(() => setByokConnecting(key), 800 + i * 1250);
        later(() => {
          setByokConnecting((c) => (c === key ? null : c));
          setByokConnected((prevC) => (prevC.includes(key) ? prevC : [...prevC, key]));
        }, 800 + i * 1250 + 720);
      });
    } else if (id === 'arena') {
      later(() => setArenaPhase('typing'), 600);
    } else if (id === 'workingset') {
      later(() => setWsExcluded(['test_auth.py']), 1900);
      later(() => setWsExcluded([]), 4400);
    } else if (id === 'memory') {
      [0, 1, 2, 3].forEach((mi, i) => later(() => setMemActive(mi), 700 + i * 1500));
    } else if (id === 'workflows') {
      TOUR_WORKFLOWS.forEach((w, i) => {
        later(() => { setWfActive(w.id); setWfAction(null); }, 700 + i * 1700);
        later(() => setWfAction(0), 700 + i * 1700 + 900);
      });
    } else if (id === 'media') {
      later(() => setMediaPhase('gen'), 700);
      later(() => setMediaPhase('done'), 2400);
      later(() => setMediaTab('video'), 4300);
      later(() => setMediaTab('voice'), 5900);
    } else if (id === 'cost') {
      later(() => setCostPeriod('1M'), 1700);
      later(() => setCostPeriod('1D'), 3600);
      later(() => setCostPeriod('7D'), 5500);
    }
    return clearTimers;
  }, [effectivePlaying, step.id, providers, later, clearTimers]);

  // Keyboard nav
  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight') { takeControl(); next(); }
    else if (e.key === 'ArrowLeft') { takeControl(); prev(); }
  }, [next, prev, takeControl]);

  // User interaction handlers (take control, then act)
  const handleConnect = (key) => {
    takeControl();
    setByokConnecting(key);
    later(() => {
      setByokConnecting((c) => (c === key ? null : c));
      setByokConnected((prevC) => (prevC.includes(key) ? prevC : [...prevC, key]));
    }, 650);
  };
  const handleArenaPromptChange = (val) => { takeControl(); setArenaPhase('idle'); setArenaWinner(null); setArenaPrompt(val); };
  const handleArenaFocus = () => { takeControl(); };
  const handleArenaRun = () => {
    takeControl();
    setArenaWinner(null);
    setArenaStream(0);
    setArenaPhase('running');
  };
  const handleArenaVote = (idx) => { takeControl(); setArenaWinner(idx); };
  const handleWsToggle = (name) => {
    takeControl();
    setWsExcluded((ex) => (ex.includes(name) ? ex.filter((n) => n !== name) : [...ex, name]));
  };
  const handleMemPick = (id) => { takeControl(); setMemActive(id); };
  const handleWfPick = (id) => { takeControl(); setWfActive(id); setWfAction(null); };
  const handleWfActionPick = (idx) => { takeControl(); setWfAction(idx); };
  const handleMediaTab = (id) => { takeControl(); setMediaTab(id); };
  const handleMediaGenerate = () => {
    takeControl();
    setMediaPhase('gen');
    later(() => setMediaPhase('done'), 1600);
  };
  const handleCostPeriod = (p) => { takeControl(); setCostPeriod(p); };

  const renderPanel = () => {
    switch (step.id) {
      case 'byok':
        return <ByokPanel providers={providers} connected={byokConnected} connecting={byokConnecting} onConnect={handleConnect} />;
      case 'arena':
        return (
          <ArenaPanel
            providers={providers}
            prompt={arenaPrompt}
            phase={arenaPhase}
            stream={arenaStream}
            winner={arenaWinner}
            onPromptChange={handleArenaPromptChange}
            onFocus={handleArenaFocus}
            onRun={handleArenaRun}
            onVote={handleArenaVote}
          />
        );
      case 'workingset':
        return <WorkingSetPanel excluded={wsExcluded} onToggle={handleWsToggle} />;
      case 'memory':
        return <MemoryPanel active={memActive} onPick={handleMemPick} />;
      case 'workflows':
        return (
          <WorkflowsPanel
            active={wfActive}
            action={wfAction}
            onPick={handleWfPick}
            onActionPick={handleWfActionPick}
          />
        );
      case 'media':
        return <MediaPanel tab={mediaTab} phase={mediaPhase} onTab={handleMediaTab} onGenerate={handleMediaGenerate} />;
      case 'cost':
        return <CostPanel period={costPeriod} onPeriod={handleCostPeriod} />;
      case 'outro':
        return <OutroPanel />;
      default:
        return null;
    }
  };

  return (
    <div className="tpt-wrapper" ref={rootRef} onKeyDown={onKeyDown}>
      <div className="tpt-window">
        {/* Title bar */}
        <div className="tpt-titlebar">
          <div className="tpt-dots"><span className="tpt-dot red" /><span className="tpt-dot yellow" /><span className="tpt-dot green" /></div>
          <div className="tpt-title">TensorPilot</div>
          <div className="tpt-titlebar-right">
            <FontAwesomeIcon icon={faLock} className="tpt-i-green" />
            <span className="tpt-status">Encrypted local storage</span>
            <a href="/download" className="tpt-get-link">Get TensorPilot</a>
          </div>
        </div>

        {/* Body: caption + stage */}
        <div className="tpt-body">
          <div className="tpt-caption" aria-live="polite">
            <span className="tpt-step-eyebrow">Step {Math.min(activeStep + 1, stepCount)} of {stepCount}</span>
            <h3 className="tpt-headline">{step.headline}</h3>
            <p className="tpt-sub">{step.sub}</p>
            <span className="tpt-chip"><FontAwesomeIcon icon={faCheck} /> {step.chip}</span>
            {step.id !== 'outro' && (
              <Link to="/download" className="tpt-caption-cta">Download TensorPilot <FontAwesomeIcon icon={faArrowRight} /></Link>
            )}
          </div>

          <div className="tpt-stage" key={step.id}>
            {renderPanel()}
          </div>
        </div>

        {/* Controls */}
        <div className="tpt-controls">
          <button type="button" className="tpt-ctrl-btn" onClick={() => { takeControl(); prev(); }} aria-label="Previous step">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            type="button"
            className="tpt-ctrl-btn tpt-play"
            onClick={() => setIsPlaying((p) => !p)}
            aria-label={isPlaying ? 'Pause tour' : 'Play tour'}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>
          <div className="tpt-dots-nav" role="tablist" aria-label="Tour steps">
            {steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={i === activeStep}
                aria-label={s.tab}
                className={`tpt-dot-nav ${i === activeStep ? 'is-active' : ''}`}
                onClick={() => { takeControl(); goTo(i); }}
              >
                <FontAwesomeIcon icon={s.icon} className="tpt-dot-ico" />
                <span className="tpt-dot-label">{s.tab}</span>
                {i === activeStep && effectivePlaying && (
                  <span className="tpt-dot-progress" style={{ width: `${progress}%` }} />
                )}
              </button>
            ))}
          </div>
          <button type="button" className="tpt-ctrl-btn" onClick={() => { takeControl(); next(); }} aria-label="Next step">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveAppTour;
